import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import React, { ReactElement, useReducer, useContext, createContext } from 'react';

import { retryReducer } from '@reducers/payment-retry/retry-data';
import { RetryActions, RetryState } from '@reducers/payment-retry/types';
import { RetryPaymentStatus } from '@types-internal';
import { MergedRetryData, RetryProcessAuthorisationResponse } from '@types-internal/RetryTypes';

interface RetryDataContextProps {
  retryState: RetryState;
  retrySuccessWithData: (retryData: MergedRetryData) => void;
  initiateRetryPaymentStart: () => void;
  initiateRetryPaymentFailure: () => void;
  initiateRetryPaymentSuccess: (
    actionObject: PaymentAction,
    paymentId: string,
    retryPaymentStatus: RetryPaymentStatus,
  ) => void;
  processPaymentAuthorisationStart: () => void;
  processPaymentAuthorisationFailure: () => void;
  processPaymentAuthorisationSuccess: (
    authorisationResult: RetryProcessAuthorisationResponse,
  ) => void;
}

export const RetryDataContext = createContext<RetryDataContextProps>({
  retryState: {
    retryData: undefined,
    initiatePaymentData: undefined,
    processAuthorisationData: undefined,
    customerId: undefined,
  },
  retrySuccessWithData: () => null,
  initiateRetryPaymentStart: () => null,
  initiateRetryPaymentFailure: () => null,
  initiateRetryPaymentSuccess: () => null,
  processPaymentAuthorisationStart: () => null,
  processPaymentAuthorisationFailure: () => null,
  processPaymentAuthorisationSuccess: () => null,
});

type Props = { customerId: string };

export const RetryDataProvider: React.FC<Props> = ({ children, customerId }): ReactElement => {
  const [state, dispatch] = useReducer(retryReducer, {
    retryData: undefined,
    initiatePaymentData: undefined,
    processAuthorisationData: undefined,
    customerId,
  });

  const retrySuccessWithData = (retryData: MergedRetryData) => {
    dispatch({ type: RetryActions.retry_fetch_success, payload: retryData });
  };

  const initiateRetryPaymentStart = () => {
    dispatch({ type: RetryActions.initiate_payment_start });
  };

  const initiateRetryPaymentFailure = () => {
    dispatch({ type: RetryActions.initiate_payment_failure });
  };

  const initiateRetryPaymentSuccess = (
    actionObject: PaymentAction,
    paymentId: string,
    retryPaymentStatus: RetryPaymentStatus,
    authorizationId?: string,
  ) => {
    dispatch({
      type: RetryActions.initiate_payment_success,
      payload: { action: actionObject, paymentId, retryPaymentStatus, authorizationId },
    });
  };

  const processPaymentAuthorisationStart = () => {
    dispatch({ type: RetryActions.process_payment_authorisation_start });
  };

  const processPaymentAuthorisationFailure = () => {
    dispatch({ type: RetryActions.process_payment_authorisation_failure });
  };

  const processPaymentAuthorisationSuccess = (
    authorisationResult: RetryProcessAuthorisationResponse,
  ) => {
    dispatch({
      type: RetryActions.process_payment_authorisation_success,
      payload: authorisationResult,
    });
  };

  return (
    <RetryDataContext.Provider
      value={{
        retryState: state,
        retrySuccessWithData,
        initiateRetryPaymentFailure,
        initiateRetryPaymentStart,
        initiateRetryPaymentSuccess,
        processPaymentAuthorisationStart,
        processPaymentAuthorisationFailure,
        processPaymentAuthorisationSuccess,
      }}>
      {children}
    </RetryDataContext.Provider>
  );
};

export const useRetryState = () => {
  return useContext(RetryDataContext);
};

export default RetryDataProvider;
